/**
 * useQuoteMargins — React hook that wraps the margin engine for the Quote Builder.
 *
 * Provides:
 * - Reactive computation of all line items, cost breakdown, and quote totals
 * - Two-way binding: edit margin% → price updates; edit price → margin% updates
 * - Integration with Settings (overall margin) and catalog (category margins)
 * - All M-1 through M-7 rules enforced automatically
 *
 * Usage:
 *   const { computedLines, summary, updateLineMargin } = useQuoteMargins(lineItems, settings);
 */

import { useMemo, useCallback } from 'react';
import {
  computeQuote,
  formatMarginPct,
  parseMarginPct,
} from './marginEngine';

/**
 * @param {Array<object>} lineItems - Raw line items from quote state
 * @param {object} options
 * @param {number} [options.overallMargin=0.35] - User-entered quote margin (decimal;
 *   Lindsey level 4 / PT-30 level 5). Kept as `overallMargin` for backward compat;
 *   `quoteMargin` is accepted as a new-style alias.
 * @param {number} [options.quoteMargin] - Alias for `overallMargin` (PT-30 naming).
 * @param {number|null} [options.customerMargin] - Customer-specific default margin
 *   (decimal). Wired from `customer.default_margin` at PT-30 (Lindsey level 2).
 * @param {number|null} [options.customerTypeMargin] - Customer-type default margin
 *   (decimal). Wired from Settings→Customer Types (Lindsey level 3).
 * @param {number} [options.taxRate=0] - Tax rate as decimal (e.g. 0.07)
 * @param {Array<{type: string, value: number}>} [options.discounts=[]] - Builder discounts (PR-12)
 * @param {number} [options.laborBurdenPct=0] - IDI-205: account-wide labor
 *   burden as a 0-100 percent (Settings → Quote Defaults), threaded through
 *   to costBreakdown.
 * @returns {object} Computed quote data + mutation helpers
 */
export default function useQuoteMargins(
  lineItems = [],
  {
    overallMargin = 0.35,
    quoteMargin,
    customerMargin = null,
    customerTypeMargin = null,
    taxRate = 0,
    discounts = [],
    laborBurdenPct = 0,
    // §2 (RS-2, 2026-08-07): when the quote is sent/locked, readers should
    // honour the stamped `resolved_margin`/`margin_source` on each line so the
    // display matches the PDF the customer received. Drafts always resolve
    // fresh so a settings change picks up immediately.
    isSent = false,
  } = {}
) {
  // PT-30: build the full 5-level margin context object. `overallMargin` and
  // `quoteMargin` are aliases; new callers pass `quoteMargin`, legacy callers
  // still pass `overallMargin`. `resolveMargin`/`computeLineItem` handle the
  // precedence internally.
  const marginContext = useMemo(
    () => ({
      customerMargin,
      customerTypeMargin,
      quoteMargin: quoteMargin != null ? quoteMargin : overallMargin,
      preferStamp: isSent === true,
    }),
    [customerMargin, customerTypeMargin, quoteMargin, overallMargin, isSent]
  );

  // Memoize the full quote computation — recalculates only when inputs change.
  // PR-12 (2026-07-22): thread `discounts` so the Estimate rail's Bid Total
  // actually reflects the discount (was previously computed pre-discount).
  // PT-30 (2026-07-26): pass a marginContext object; the engine still supports
  // a bare number for backward compat, so this is safe.
  const quote = useMemo(
    () => computeQuote(lineItems, marginContext, taxRate, discounts, laborBurdenPct),
    [lineItems, marginContext, taxRate, discounts, laborBurdenPct]
  );

  /**
   * M-4: Update a line item's margin (recomputes price).
   * Returns a new line items array with the updated item.
   *
   * @param {number} index - Line item index
   * @param {number} newMarginPct - New margin as decimal (0.35)
   * @param {Array<object>} items - Current line items array
   * @returns {Array<object>} New array with updated item
   */
  const updateLineMargin = useCallback((index, newMarginPct, items) => {
    const updated = [...items];
    // IDI-309: drop BOTH aliases (and a target re-price's marker). Clearing
    // only lineTotal left line_total behind, which the engine reads as
    // `lineTotal ?? line_total` and would keep as a price override.
    const { line_total: _lt, marginSource: _ms, ...rest } = updated[index];
    updated[index] = {
      ...rest,
      ...(_ms && _ms !== 'price_override' ? { marginSource: _ms } : {}),
      lineMargin: newMarginPct,
      lineTotal: null, // Clear price override — margin takes precedence
    };
    return updated;
  }, []);

  /**
   * M-6: Update a custom item's cost (recomputes total with current margin).
   * Only valid for custom items (isCustom: true).
   *
   * @param {number} index - Line item index
   * @param {object} costs - { materialCost, laborCost, otherCost } in cents
   * @param {Array<object>} items - Current line items array
   * @returns {Array<object>} New array with updated item
   */
  const updateLineCost = useCallback((index, costs, items) => {
    const updated = [...items];
    const item = updated[index];
    if (!item.isCustom) {
      console.warn('M-6: Cannot edit cost on catalog items');
      return items;
    }
    updated[index] = {
      ...item,
      ...costs,
      lineTotal: null, // Recalculate from new cost + existing margin
    };
    return updated;
  }, []);

  // DEF-155 (2026-08-04): memoize summary so consumers (QuoteBuilderPage's
  // quotePayload useMemo) get a stable reference. Without this, `baseSummary`
  // is a new object every render → quotePayload recomputes → dirty-check
  // effect fires → even with stableStringify the effect runs unnecessarily.
  // This is the generator fix; layers 1-3 become belt-and-braces.
  const summary = useMemo(() => ({
    costBreakdown: quote.costBreakdown,
    blendedMargin: quote.blendedMargin,
    blendedMarginFormatted: formatMarginPct(quote.blendedMargin),
    bidSubtotal: quote.bidSubtotal,
    discountAmount: quote.discountAmount,
    taxableSubtotal: quote.taxableSubtotal,
    taxAmount: quote.taxAmount,
    bidTotal: quote.bidTotal,
    marginDollars: quote.marginDollars,
    isMarginEditable: (quote.lines || []).some(
      (l) => l && l.marginSource === 'quote_default',
    ),
  }), [quote]);

  return {
    // Computed data (read-only outputs)
    computedLines: quote.lines,
    summary,

    // Mutation helpers (return new arrays — caller updates state)
    updateLineMargin,
    // IDI-107 Part 2 (2026-08-25 ruling): updateLinePrice removed — the
    // per-line price override write affordance is gone (see
    // CommittedLineCard.js for the full rationale). It contradicted this
    // same margin engine's own precedence rule (updateLineMargin clears
    // lineTotal "margin takes precedence"; updateLinePrice cleared
    // lineMargin "price takes precedence" — whichever was called last won,
    // with no UI signal either way).
    updateLineCost,

    // Re-exported utilities
    formatMarginPct,
    parseMarginPct,
  };
}
